import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { HOME_CTA_COPY, type FaqCtaSlot } from '@/content/home-cta/copy';
import { useSignupUrl } from '@/hooks/useSignupUrl';
import { useTranslation } from '@/i18n/i18n';
import { isProxyOwnedLegalPath, localePath } from '@/i18n/paths';
import { trackLandingCtaClicked } from '@/lib/analytics';

/**
 * The objection-answer CTA directly under an open FAQ answer (spec 01 §2). One per
 * panel; it lives inside the accordion content, so it is hidden while collapsed.
 * No copy for the locale = no CTA.
 */
export const FaqCta = ({ slot }: { slot: FaqCtaSlot }) => {
  const { locale, t } = useTranslation();
  const signupUrl = useSignupUrl({
    entityType: 'jdg',
    source: slot,
    placement: slot,
    page: 'home',
  });
  const copy = HOME_CTA_COPY[locale]?.faq[slot];
  if (!copy) return null;

  const ctaLabel = t('hero.ctaPrimary');
  const micro = copy.micro === 'price' ? t('pricing.betaFree') : t('pricing.honestySubline');
  const linkTarget =
    copy.link && copy.link.href !== '#pricing' ? localePath(copy.link.href, locale) : null;

  return (
    <div
      data-testid={`faq-cta-${slot}`}
      className="mt-5 rounded-xl border border-primary/30 bg-primary/5 p-4 text-foreground"
    >
      <p className="font-semibold">{copy.headline}</p>
      {copy.lead && <p className="mt-1 text-sm text-muted-foreground">{copy.lead}</p>}
      {copy.stanceBody && (
        <p className="mt-1 text-sm text-muted-foreground">{t('pricing.honestySubline')}</p>
      )}
      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
        <Button className="h-12 rounded-xl px-6 text-base font-bold" asChild>
          <a
            href={signupUrl}
            data-testid={`faq-cta-${slot}-button`}
            onClick={() =>
              trackLandingCtaClicked(slot, ctaLabel, {
                source: slot,
                placement: slot,
                page: 'home',
                locale,
                entity_type: 'jdg',
                faq_open: true,
              })
            }
          >
            {ctaLabel}
          </a>
        </Button>
        {copy.link &&
          (linkTarget ? (
            <Link
              to={linkTarget}
              reloadDocument={isProxyOwnedLegalPath(linkTarget)}
              className="text-sm underline underline-offset-4 hover:no-underline"
            >
              {copy.link.label}
            </Link>
          ) : (
            <a href="#pricing" className="text-sm underline underline-offset-4 hover:no-underline">
              {copy.link.label}
            </a>
          ))}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">{micro}</p>
    </div>
  );
};
